import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import API from '../api';
import { useAuth } from './AuthContext';
import { toast } from 'react-toastify';

const WishlistContext = createContext();

export const useWishlist = () => useContext(WishlistContext);

export const WishlistProvider = ({ children }) => {
  const { user } = useAuth();
  const [wishlistIds, setWishlistIds] = useState(new Set()); // Set of product IDs
  const [wishlistItems, setWishlistItems] = useState([]); // Full populated items
  const [loading, setLoading] = useState(false);

  // Fetch wishlist when user logs in
  const fetchWishlist = useCallback(async () => {
    if (!user) {
      setWishlistIds(new Set());
      setWishlistItems([]);
      return;
    }
    try {
      setLoading(true);
      const { data } = await API.get('/api/wishlist');
      if (data.success) {
        const items = data.items || [];
        setWishlistItems(items);
        setWishlistIds(new Set(items.map((item) => item.product?._id || item.product)));
      }
    } catch (error) {
      console.error('Failed to fetch wishlist:', error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchWishlist();
  }, [fetchWishlist]);

  // Check if a product is in wishlist
  const isInWishlist = useCallback((productId) => {
    return wishlistIds.has(productId);
  }, [wishlistIds]);

  // Toggle wishlist (add/remove) with optimistic UI
  const toggleWishlist = useCallback(async (productId, productName) => {
    if (!user) {
      toast.error('Please login to use wishlist');
      return;
    }

    const wasInWishlist = wishlistIds.has(productId);

    // Optimistic update
    setWishlistIds((prev) => {
      const next = new Set(prev);
      if (wasInWishlist) {
        next.delete(productId);
      } else {
        next.add(productId);
      }
      return next;
    });

    try {
      if (wasInWishlist) {
        await API.delete(`/api/wishlist/remove/${productId}`);
        setWishlistItems((prev) =>
          prev.filter((item) => (item.product?._id || item.product) !== productId)
        );
        toast.info(`Removed from wishlist`);
      } else {
        await API.post('/api/wishlist/add', { productId });
        toast.success(`${productName || 'Product'} added to wishlist ❤️`);
        // Refetch to get full populated data
        fetchWishlist();
      }
    } catch (error) {
      // Revert optimistic update on failure
      setWishlistIds((prev) => {
        const next = new Set(prev);
        if (wasInWishlist) {
          next.add(productId);
        } else {
          next.delete(productId);
        }
        return next;
      });
      const msg = error.response?.data?.message || 'Failed to update wishlist';
      if (msg !== 'Product already in wishlist') {
        toast.error(msg);
      }
    }
  }, [user, wishlistIds, fetchWishlist]);

  // Remove from wishlist (for wishlist page)
  const removeFromWishlist = useCallback(async (productId) => {
    setWishlistIds((prev) => {
      const next = new Set(prev);
      next.delete(productId);
      return next;
    });
    setWishlistItems((prev) =>
      prev.filter((item) => (item.product?._id || item.product) !== productId)
    );

    try {
      await API.delete(`/api/wishlist/remove/${productId}`);
    } catch (error) {
      fetchWishlist(); // Revert on error
    }
  }, [fetchWishlist]);

  // Move to cart (remove from wishlist server-side, add to cart on frontend)
  const moveToCart = useCallback(async (productId) => {
    try {
      await API.post(`/api/wishlist/move-to-cart/${productId}`);
      setWishlistIds((prev) => {
        const next = new Set(prev);
        next.delete(productId);
        return next;
      });
      setWishlistItems((prev) =>
        prev.filter((item) => (item.product?._id || item.product) !== productId)
      );
      return true;
    } catch (error) {
      toast.error('Failed to move to cart');
      return false;
    }
  }, []);

  return (
    <WishlistContext.Provider
      value={{
        wishlistIds,
        wishlistItems,
        loading,
        isInWishlist,
        toggleWishlist,
        removeFromWishlist,
        moveToCart,
        fetchWishlist,
        count: wishlistIds.size,
      }}
    >
      {children}
    </WishlistContext.Provider>
  );
};
